import { CountryConfig } from "@/helpers/countryConfig";
import { SiteInfo } from "@/redux/slices/config/types";
import { CountryState } from "@/redux/slices/resource/types";
import {
  GetCountriesRawResponse,
  GetCountriesResponse,
  GetCountryStatesRawResponse,
  GetCountryStatesResponse,
  GetSiteInfoRawResponse,
  RawCountry,
} from "./types";

export const transformStates = (res: GetCountryStatesRawResponse) => {
  const states = res._embedded.state.map((i) => {
    const { uuid, country_uuid, name, image_cdn, abbreviation } = i;
    return {
      id: uuid,
      countryId: country_uuid,
      abbreviation,
      imageCdn: image_cdn,
      name,
    } as CountryState;
  });

  const response: GetCountryStatesResponse = {
    data: {
      states,
    },
    links: res._links,
  };

  return response;
};

export const transformSiteInfo = (res: GetSiteInfoRawResponse) => {
  const { domain_key, metadata, url, recaptcha_site_key } = res;

  const response: SiteInfo = {
    key: domain_key.toLowerCase(),
    isMultiTenant: metadata.is_multi_tenant,
    apiUrl: url.api,
    url: url.domain,
    recaptcha_site_key,
  };

  return response;
};

/**
 * Normalises the distance unit the API sends.
 *
 * The column has held "KM" for AU/NZ and "Mi" for the US, and `isImperialUnit` compares against
 * "mi" — so anything other than a lower-cased "mi" silently renders miles as kilometres. Cheap to
 * defend against here, invisible if it goes wrong.
 */
const normaliseDistanceUnit = (unit?: string): "km" | "mi" | undefined => {
  const value = (unit ?? "").trim().toLowerCase();

  if (value === "mi" || value === "mile" || value === "miles") return "mi";
  if (value === "km" || value === "kilometre" || value === "kilometer") return "km";

  return undefined;
};

/**
 * Keys countries by code and camelCases them, omitting anything the backend did not send so the
 * per-field merge in resolveCountryConfig falls back for just that field.
 */
export const transformCountries = (res: GetCountriesRawResponse): GetCountriesResponse => {
  const countries = res?._embedded?.country ?? [];

  return countries.reduce<GetCountriesResponse>((acc, country: RawCountry) => {
    const code = (country.country_code ?? "").toUpperCase();
    if (!code) return acc;

    const config: Partial<CountryConfig> = {
      countryCode: code,
      name: country.name,
      currency: country.currency,
      distanceUnit: normaliseDistanceUnit(country.distance_unit),
      postalLabel: country.postal_label,
      regionLabel: country.region_label,
      bankRoutingLabel: country.bank_routing_label,
      bankRoutingScheme: country.bank_routing_scheme as CountryConfig["bankRoutingScheme"],
      businessRegistrationTypes: country.business_registration_types,
      taxLabel: country.tax_label,
      phoneDialCode: country.phone_dial_code,
      defaultTimezone: country.default_timezone,
      // Only when the country's payment vendor is actually Stripe. The field is named after the
      // vendor because that is what a caller does with it — hands it to loadStripe — and handing
      // an Airwallex key to loadStripe would fail somewhere far from here.
      stripePublishableKey: country.integrations?.payments?.vendor === "stripe" ? country.integrations.payments.publishable_key : undefined,
    };

    acc[code] = config;
    return acc;
  }, {});
};
